import React, { useState } from 'react';
import {
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

// 五条主曲线的 dataKey
const SERIES_KEYS = ['fps', 'bitrate', 'rtt', 'packetLoss', 'jitterBuffer'] as const;
type SeriesKey = typeof SERIES_KEYS[number];
type SeriesHidden = Record<SeriesKey, boolean>;

interface TimelinePoint {
  time: number;       // 0..N，序号
  bitrate: number;    // kbps（接收侧）
  fps: number;
  rtt: number;
  packetLoss: number; // %
  jitterBuffer: number; // ms
  // Stage 3D 新增（可选，旧 chartData 兼容）
  qp?: number;
  encodeMs?: number;
  captureLagMs?: number;
  utilizationPct?: number;
}

interface EventMarker {
  time: number;       // 同时间轴坐标
  label: string;      // 'SUDDEN_DROP' / 'KEYFRAME' / 'CPU_OVERLOAD' 等
  color: string;      // hex
}

interface MainTimelineProps {
  data: TimelinePoint[];
  events?: EventMarker[];
  // 可选的额外曲线数据（Stage 2C.4 联动时使用，暂时空实现）
  overlay?: { dataKey: string; color: string; yAxisId?: 'left' | 'right' | 'loss' }[];
  // Stage 3D: 时间窗口标识（影响图表标题显示）
  timeWindow?: '60s' | '5min';
}

// 可点击图例：点击切换对应曲线显示/隐藏
const LegendDotButton: React.FC<{
  color: string;
  label: string;
  hidden: boolean;
  onClick: () => void;
}> = ({ color, label, hidden, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={hidden ? '点击显示' : '点击隐藏'}
    className={`flex items-center gap-1.5 text-[11px] cursor-pointer transition-opacity hover:opacity-80 ${
      hidden ? 'opacity-40 line-through' : 'text-zinc-300'
    }`}
  >
    <span
      className="w-2.5 h-2.5 rounded-full"
      style={{ backgroundColor: color, opacity: hidden ? 0.3 : 1 }}
    />
    {label}
  </button>
);

// overlay 图例不参与隐藏交互（保持原 span 行为）
const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span className="flex items-center gap-1.5 text-[11px] text-zinc-300">
    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
    {label}
  </span>
);

const MainTimeline: React.FC<MainTimelineProps> = ({ data, events = [], overlay = [], timeWindow = '60s' }) => {
  const [hidden, setHidden] = useState<SeriesHidden>({
    fps: false,
    bitrate: false,
    rtt: false,
    packetLoss: false,
    jitterBuffer: false,
  });
  const toggle = (k: SeriesKey) => setHidden(h => ({ ...h, [k]: !h[k] }));

  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="text-xs font-bold tracking-wide text-zinc-300 uppercase">
          主时间线
          <span className="ml-2 text-[10px] text-zinc-500 normal-case font-normal">
            （{timeWindow === '60s' ? '最近 60 秒' : '最近 5 分钟'} · {data.length} 个采样点）
          </span>
        </h3>
        <div className="flex flex-wrap gap-3">
          <LegendDotButton color="#10b981" label="FPS"        hidden={hidden.fps}          onClick={() => toggle('fps')} />
          <LegendDotButton color="#3b82f6" label="码率 (kbps)" hidden={hidden.bitrate}      onClick={() => toggle('bitrate')} />
          <LegendDotButton color="#8b5cf6" label="RTT (ms)"   hidden={hidden.rtt}          onClick={() => toggle('rtt')} />
          <LegendDotButton color="#ef4444" label="丢包 (%)"   hidden={hidden.packetLoss}   onClick={() => toggle('packetLoss')} />
          <LegendDotButton color="#f59e0b" label="抖动 (ms)"  hidden={hidden.jitterBuffer} onClick={() => toggle('jitterBuffer')} />
          {overlay.map((o) => (
            <LegendDot key={o.dataKey} color={o.color} label={o.dataKey} />
          ))}
        </div>
      </div>

      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              hide
              type="number"
              domain={['dataMin', 'dataMax']}
            />
            {/* 三个 Y 轴：左 fps/rtt/jitter，右 bitrate，loss% 0-100 */}
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
              width={32}
              domain={[0, 'auto']}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
              width={48}
              domain={[0, 'auto']}
            />
            <YAxis yAxisId="loss" hide domain={[0, 100]} />

            <Tooltip
              contentStyle={{
                background: 'rgba(0,0,0,0.85)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                fontSize: '11px',
              }}
              labelStyle={{ display: 'none' }}
              formatter={(value: number, name: string) => {
                if (name === 'fps')          return [`${value} FPS`, '帧率'];
                if (name === 'bitrate')      return [`${(value / 1000).toFixed(2)} Mbps`, '码率'];
                if (name === 'rtt')          return [`${value} ms`, 'RTT'];
                if (name === 'packetLoss')   return [`${value.toFixed(2)}%`, '丢包'];
                if (name === 'jitterBuffer') return [`${value.toFixed(1)} ms`, '抖动'];
                // Stage 3D: 新字段 hover 显示
                if (name === 'qp')           return [value < 0 ? 'N/A' : value, 'QP'];
                if (name === 'encodeMs')     return [`${value.toFixed(2)} ms`, '编码耗时'];
                if (name === 'captureLagMs') return [`${value.toFixed(1)} ms`, '采集→编码 lag'];
                if (name === 'utilizationPct') return [`${value}%`, 'BWE 利用率'];
                return [value, name];
              }}
            />

            {/* 5 条主曲线（hide 由图例点击控制）*/}
            <Line yAxisId="left"  type="monotone" dataKey="fps"          stroke="#10b981" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hidden.fps} />
            <Line yAxisId="right" type="monotone" dataKey="bitrate"      stroke="#3b82f6" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hidden.bitrate} />
            <Line yAxisId="left"  type="monotone" dataKey="rtt"          stroke="#8b5cf6" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hidden.rtt} />
            <Line yAxisId="loss"  type="monotone" dataKey="packetLoss"   stroke="#ef4444" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hidden.packetLoss} />
            <Line yAxisId="left"  type="monotone" dataKey="jitterBuffer" stroke="#f59e0b" strokeWidth={1.5} dot={false} isAnimationActive={false} hide={hidden.jitterBuffer} />

            {/* 联动叠加曲线（Stage 2C.4 用）*/}
            {overlay.map((o) => (
              <Line
                key={o.dataKey}
                yAxisId={o.yAxisId || 'left'}
                type="monotone"
                dataKey={o.dataKey}
                stroke={o.color}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            ))}

            {/* 事件标记带（垂直虚线 + 标签）*/}
            {events.map((ev, idx) => (
              <ReferenceLine
                key={idx}
                x={ev.time}
                yAxisId="left"
                stroke={ev.color}
                strokeDasharray="2 4"
                strokeOpacity={0.6}
                label={{
                  value: ev.label,
                  position: 'insideTop',
                  fill: ev.color,
                  fontSize: 9,
                }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MainTimeline;
